import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../api/client';
import type {
  AdminRosterMember,
  AdminRosterUpdate,
  AuditEntityType,
  AuditLogResponse,
  DealAliasCreateRequest,
  DealAliasListResponse,
  DealAliasMatchMode,
  DealAliasRow,
  DealResolveSummaryResponse,
  FundSettings,
  ImpersonatableUser,
  LlmSpendBucket,
  LlmSpendSeries,
  LlmSpendWindow,
  LlmUsageOverview,
  MissedBindingsResponse,
  MyTabsResponse,
  NotifyResult,
  Schedule,
  ScheduleActionCatalogItem,
  ScheduleCreate,
  ScheduleRunStatus,
  ScheduleRunsResponse,
  ScheduleUpdate,
  SignalTriageResponse,
  TabRole,
  TabVisibilityRow,
} from '../types';

export interface SignalsTriageFilters {
  sinceDays?: number;
  status?: string;
  discardReason?: string;
  resolveMethod?: string;
  /** Filter affinity_match rows by which extraction channel found the candidate.
   *  'codename' | 'parenthetical' | 'sender_domain'. Spec §6.9 enrichment. */
  extractionSource?: string;
  /** 'inbound' | 'outbound' | 'calendar' */
  sourceType?: string;
  /** Case-insensitive substring; LIKE metacharacters escaped server-side. */
  subjectContains?: string;
  sender?: string;
  userEmail?: string;
  limit?: number;
}

export function useSignalsTriage(filters: SignalsTriageFilters = {}) {
  const params = new URLSearchParams();
  if (filters.sinceDays != null) params.set('since_days', String(filters.sinceDays));
  if (filters.status) params.set('status', filters.status);
  if (filters.discardReason) params.set('discard_reason', filters.discardReason);
  if (filters.resolveMethod) params.set('resolve_method', filters.resolveMethod);
  if (filters.extractionSource) params.set('extraction_source', filters.extractionSource);
  if (filters.sourceType) params.set('source_type', filters.sourceType);
  if (filters.subjectContains) params.set('subject_contains', filters.subjectContains);
  if (filters.sender) params.set('sender_contains', filters.sender);
  if (filters.userEmail) params.set('user_email', filters.userEmail);
  if (filters.limit != null) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return useQuery<SignalTriageResponse>({
    queryKey: ['signals-triage', filters],
    queryFn: () =>
      apiFetch<SignalTriageResponse>(`/api/admin/signals-triage${qs ? `?${qs}` : ''}`),
    staleTime: 30 * 1000,
  });
}

/**
 * Deal-resolve telemetry summary for the Engine tab's diagnostic panel.
 * Aggregates the PR-1 shadow-logging columns over a configurable lookback.
 * Admin-only on the server.
 */
export function useDealResolveSummary(sinceDays: number) {
  return useQuery<DealResolveSummaryResponse>({
    queryKey: ['admin', 'deal-resolve-summary', sinceDays],
    queryFn: () =>
      apiFetch<DealResolveSummaryResponse>(
        `/api/admin/deal-resolve/summary?since_days=${sinceDays}`,
      ),
    staleTime: 60 * 1000,
  });
}

/**
 * Recent no_match signals whose subject/sender suggests they should have
 * bound. Spec §6.9 audit feeder. Admin-only.
 */
export function useDealResolveMissedBindings(sinceDays: number, limit = 50) {
  return useQuery<MissedBindingsResponse>({
    queryKey: ['admin', 'deal-resolve-missed-bindings', sinceDays, limit],
    queryFn: () =>
      apiFetch<MissedBindingsResponse>(
        `/api/admin/deal-resolve/missed-bindings?since_days=${sinceDays}&limit=${limit}`,
      ),
    staleTime: 60 * 1000,
  });
}

// --- Deal aliases (spec §6.10) -------------------------------------------

export interface DealAliasFilters {
  isActive?: boolean;
  source?: string;
  limit?: number;
}

/**
 * Admin alias-review list. Spec §6.10.7.
 * Default-sorted active-first by last_matched_at desc so dead aliases sink.
 */
export function useDealAliases(filters: DealAliasFilters = {}) {
  const params = new URLSearchParams();
  if (filters.isActive !== undefined) {
    params.set('is_active', filters.isActive ? 'true' : 'false');
  }
  if (filters.source) params.set('source', filters.source);
  if (filters.limit != null) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return useQuery<DealAliasListResponse>({
    queryKey: ['admin', 'deal-aliases', filters],
    queryFn: () =>
      apiFetch<DealAliasListResponse>(
        `/api/admin/deal-aliases${qs ? `?${qs}` : ''}`,
      ),
    staleTime: 30 * 1000,
  });
}

/**
 * Bad-alias kill switch. Admin-only. The resolver immediately stops
 * matching against this alias after the call returns. Spec §6.10.7.
 */
// ── LLM spend governance ────────────────────────────────────────────────────

/** Release the API-error (EnrichmentDeferred) enrichment-queue defers so the next drain tick retries
 *  them now — 'the provider is back, run these now'. */
export function useReleaseEnrichmentDefers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ released: number } & LlmUsageOverview>('/api/admin/llm/enrichment-defers/release', {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'llm-usage'] });
      qc.invalidateQueries({ queryKey: ['crm'] });
    },
  });
}

export function useSetWebBudget() {
  const qc = useQueryClient();
  return useMutation<LlmUsageOverview, Error, number>({
    mutationFn: (dailyCapUsd) =>
      apiFetch<LlmUsageOverview>('/api/admin/llm/web-budget', {
        method: 'PATCH',
        body: JSON.stringify({ daily_cap_usd: dailyCapUsd }),
      }),
    onSuccess: (data) => qc.setQueryData(['admin', 'llm-usage'], data),
  });
}

export function useLlmUsage(enabled = true) {
  return useQuery<LlmUsageOverview>({
    queryKey: ['admin', 'llm-usage'],
    queryFn: () => apiFetch<LlmUsageOverview>('/api/admin/llm/usage'),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useSetLlmBudget() {
  const queryClient = useQueryClient();
  return useMutation<LlmUsageOverview, Error, { purpose: string; dailyCapUsd: number; autoResume?: boolean }>({
    mutationFn: ({ purpose, dailyCapUsd, autoResume }) =>
      apiFetch<LlmUsageOverview>(`/api/admin/llm/budgets/${purpose}`, {
        method: 'PATCH',
        body: JSON.stringify({ daily_cap_usd: dailyCapUsd, auto_resume: autoResume ?? null }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'llm-usage'] }),
  });
}

export function useResumeLlmBudget() {
  const queryClient = useQueryClient();
  return useMutation<{ purpose: string; cleared: number }, Error, string>({
    mutationFn: (purpose) =>
      apiFetch<{ purpose: string; cleared: number }>(`/api/admin/llm/budgets/${purpose}/resume`, {
        method: 'POST',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'llm-usage'] }),
  });
}

export function useSetGlobalCeiling() {
  const queryClient = useQueryClient();
  return useMutation<LlmUsageOverview, Error, number>({
    mutationFn: (ceilingUsd) =>
      apiFetch<LlmUsageOverview>('/api/admin/llm/global-ceiling', {
        method: 'PATCH',
        body: JSON.stringify({ ceiling_usd: ceilingUsd }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'llm-usage'] }),
  });
}

export interface LlmSpendSeriesParams {
  window: LlmSpendWindow;
  bucket: LlmSpendBucket;
  start?: string;
  end?: string;
}

export function useLlmSpendSeries(params: LlmSpendSeriesParams, enabled = true) {
  const { window, bucket, start, end } = params;
  const isCustom = window === 'custom';
  return useQuery<LlmSpendSeries>({
    // window/bucket/dates in the key → changing a control refetches.
    queryKey: ['admin', 'llm-spend-series', window, bucket, start ?? null, end ?? null],
    queryFn: () => {
      const qs = new URLSearchParams({ window, bucket });
      if (isCustom && start && end) {
        qs.set('start', start);
        qs.set('end', end);
      }
      return apiFetch<LlmSpendSeries>(`/api/admin/llm/spend-series?${qs.toString()}`);
    },
    // Don't fire a custom-window request until both dates are present (the
    // backend 422s a custom window missing start/end).
    enabled: enabled && (!isCustom || (!!start && !!end)),
    refetchInterval: 60_000,
  });
}

// ── Fund II Overview settings (admin write) ─────────────────────────────────

export function useUpdateFundSettings() {
  const queryClient = useQueryClient();
  return useMutation<FundSettings, Error, Partial<Omit<FundSettings, 'updated_by' | 'updated_at' | 'stage_probabilities'>>>({
    mutationFn: (changes) =>
      apiFetch<FundSettings>('/api/admin/fund-settings', {
        method: 'PATCH',
        body: JSON.stringify(changes),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fund-settings'] }),
  });
}

// Phase 2 — upsert the per-stage weighted-mode probabilities ({stage_value: pct}).
export function useUpdateStageProbabilities() {
  const queryClient = useQueryClient();
  return useMutation<FundSettings, Error, Record<string, number>>({
    mutationFn: (probabilities) =>
      apiFetch<FundSettings>('/api/admin/fund-settings/stage-probabilities', {
        method: 'PATCH',
        body: JSON.stringify({ probabilities }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fund-settings'] }),
  });
}

/**
 * ⭐ The operator's HAND-SEED path — the only way to create an alias since the legacy Suggestions
 * rebind UI was retired. The backend routes it through the shared `attempt_alias_seed` gates, so a
 * refused token comes back as a 409 whose `detail.message` says WHY; the form renders that inline
 * rather than swallowing it, which is the whole point of the endpoint.
 */
export function useCreateDealAlias() {
  const queryClient = useQueryClient();
  return useMutation<DealAliasRow, Error, DealAliasCreateRequest>({
    mutationFn: (body) =>
      apiFetch<DealAliasRow>('/api/admin/deal-aliases', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'deal-aliases'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'deal-resolve-summary'] });
    },
  });
}

export function useDisableDealAlias() {
  const queryClient = useQueryClient();
  return useMutation<DealAliasRow, Error, { id: number; reason: string }>({
    mutationFn: ({ id, reason }) =>
      apiFetch<DealAliasRow>(`/api/admin/deal-aliases/${id}/disable`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'deal-aliases'] });
      // Also invalidate the deal-resolve summary so the panel reflects the change
      queryClient.invalidateQueries({ queryKey: ['admin', 'deal-resolve-summary'] });
    },
  });
}

/**
 * Flip one alias between the `Project <token>` phrase rule and bare-token matching
 * (owner ruling 2026-09-02). The audited per-alias widening path — see the backend route's
 * docstring for why it is per-alias rather than a matcher setting.
 */
export function useSetDealAliasMatchMode() {
  const queryClient = useQueryClient();
  return useMutation<DealAliasRow, Error, { id: number; matchMode: DealAliasMatchMode }>({
    mutationFn: ({ id, matchMode }) =>
      apiFetch<DealAliasRow>(`/api/admin/deal-aliases/${id}/match-mode`, {
        method: 'POST',
        body: JSON.stringify({ match_mode: matchMode }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'deal-aliases'] });
    },
  });
}

export function useAdminRoster() {
  return useQuery<AdminRosterMember[]>({
    queryKey: ['admin-roster'],
    queryFn: () => apiFetch<AdminRosterMember[]>('/api/admin/roster'),
    staleTime: 30 * 1000,
  });
}

export function useImpersonatableUsers(enabled: boolean = true) {
  return useQuery<ImpersonatableUser[]>({
    queryKey: ['impersonatable-users'],
    queryFn: () => apiFetch<ImpersonatableUser[]>('/api/admin/impersonatable-users'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useUpdateRosterMember() {
  const queryClient = useQueryClient();
  return useMutation<AdminRosterMember, Error, { email: string; updates: AdminRosterUpdate }>({
    mutationFn: ({ email, updates }) =>
      apiFetch<AdminRosterMember>(`/api/admin/roster/${encodeURIComponent(email)}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-roster'] });
      // Approval edits change who receives emails, so invalidate any
      // notify-result caches too.
      queryClient.invalidateQueries({ queryKey: ['pipeline'] });
      // Outreach access flips affect /api/tabs response, so the affected user
      // (or the admin flipping their own row) sees the tab update without
      // waiting on staleTime.
      queryClient.invalidateQueries({ queryKey: ['my-tabs'] });
    },
  });
}

/**
 * Bulk roster patch — fires one PATCH per row in parallel and invalidates
 * the roster cache once everything settles. Backs the inline-edit grid so
 * Save can flush all pending tick-mark changes in one shot.
 */
export function useBulkUpdateRosterMembers() {
  const queryClient = useQueryClient();
  return useMutation<
    { email: string; ok: boolean; error?: string }[],
    Error,
    { email: string; updates: AdminRosterUpdate }[]
  >({
    mutationFn: async (changes) => {
      const results = await Promise.all(
        changes.map(async ({ email, updates }) => {
          try {
            await apiFetch<AdminRosterMember>(
              `/api/admin/roster/${encodeURIComponent(email)}`,
              { method: 'PATCH', body: JSON.stringify(updates) },
            );
            return { email, ok: true };
          } catch (err) {
            return { email, ok: false, error: (err as Error).message };
          }
        }),
      );
      return results;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-roster'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline'] });
      // Bulk roster edits can include outreach access flips — invalidate
      // /api/tabs for the admin immediately, and for affected users on
      // their next mount.
      queryClient.invalidateQueries({ queryKey: ['my-tabs'] });
    },
  });
}

/**
 * Test send — every per-person scorecard is rerouted to Brad's inbox with
 * the original recipient prefixed in the subject. Safe to fire repeatedly.
 */
export function useTestNotifyAll() {
  return useMutation<NotifyResult>({
    mutationFn: () =>
      apiFetch<NotifyResult>(
        '/api/pipeline/notify?recipients=all&test_mode=true',
        { method: 'POST' },
      ),
  });
}

/**
 * Real send — every approved person gets their own scorecard. Hidden behind
 * a confirm modal in the UI so it isn't a one-click broadcast.
 *
 * `bccAdmin` (default false) BCCs ADMIN_EMAILS on each per-person email so
 * the admin can audit what went out without forcing the recipient to know.
 */
export function useNotifyAll() {
  return useMutation<NotifyResult, Error, { bccAdmin?: boolean } | void>({
    mutationFn: (vars) => {
      const bccAdmin = vars && 'bccAdmin' in vars ? vars.bccAdmin : false;
      const qs = bccAdmin ? 'recipients=all&bcc_admin=true' : 'recipients=all';
      return apiFetch<NotifyResult>(`/api/pipeline/notify?${qs}`, { method: 'POST' });
    },
  });
}

// --- Tab visibility ---

export function useMyTabs() {
  return useQuery<MyTabsResponse>({
    queryKey: ['my-tabs'],
    queryFn: () => apiFetch<MyTabsResponse>('/api/tabs'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTabVisibilityConfig() {
  return useQuery<TabVisibilityRow[]>({
    queryKey: ['tab-visibility'],
    queryFn: () => apiFetch<TabVisibilityRow[]>('/api/admin/tab-visibility'),
    staleTime: 30 * 1000,
  });
}

export function useUpdateTabVisibility() {
  const qc = useQueryClient();
  return useMutation<
    TabVisibilityRow,
    Error,
    { tab_key: string; min_role?: TabRole; globally_hidden?: boolean }
  >({
    mutationFn: ({ tab_key, min_role, globally_hidden }) => {
      const body: Record<string, unknown> = {};
      if (min_role !== undefined) body.min_role = min_role;
      if (globally_hidden !== undefined) body.globally_hidden = globally_hidden;
      return apiFetch<TabVisibilityRow>(
        `/api/admin/tab-visibility/${encodeURIComponent(tab_key)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tab-visibility'] });
      qc.invalidateQueries({ queryKey: ['my-tabs'] });
    },
  });
}


export function useReorderTabVisibility() {
  const qc = useQueryClient();
  return useMutation<
    TabVisibilityRow[],
    Error,
    { fields: Array<{ tab_key: string; display_order: number }> }
  >({
    mutationFn: (payload) =>
      apiFetch<TabVisibilityRow[]>('/api/admin/tab-visibility-order', {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tab-visibility'] });
      qc.invalidateQueries({ queryKey: ['my-tabs'] });
    },
  });
}

// --- Schedules (in-app cron) ---

/**
 * The catalog of runnable scheduler actions, sourced from the backend
 * `ACTION_HANDLERS` registry (`GET /api/admin/schedule-actions`). Drives the
 * ScheduleEditModal picker AND the SchedulesSection label lookup, so a new
 * backend action auto-appears with no frontend edit. Long staleTime — the set
 * of actions only changes on deploy.
 */
export function useScheduleActions() {
  return useQuery<ScheduleActionCatalogItem[]>({
    queryKey: ['admin-schedule-actions'],
    queryFn: () => apiFetch<ScheduleActionCatalogItem[]>('/api/admin/schedule-actions'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * List all schedules with computed `next_fire_at`. Refetched every minute so
 * "Next: in 3min" countdowns stay roughly current without per-tick polling.
 * The actual scheduler ticks every 30s on the server; the UI doesn't need
 * sub-minute precision here.
 */
export function useSchedules() {
  return useQuery<Schedule[]>({
    queryKey: ['admin-schedules'],
    queryFn: () => apiFetch<Schedule[]>('/api/admin/schedules'),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation<Schedule, Error, ScheduleCreate>({
    mutationFn: (body) =>
      apiFetch<Schedule>('/api/admin/schedules', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-schedules'] }),
  });
}

export function useUpdateSchedule() {
  const qc = useQueryClient();
  return useMutation<Schedule, Error, { id: number; updates: ScheduleUpdate }>({
    mutationFn: ({ id, updates }) =>
      apiFetch<Schedule>(`/api/admin/schedules/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-schedules'] }),
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) =>
      apiFetch<void>(`/api/admin/schedules/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-schedules'] }),
  });
}

export interface SchedulerStatus {
  last_tick_at: string | null;
  in_flight_count: number;
  in_flight_schedule_ids: number[];
  tick_interval_seconds: number;
  disabled: boolean;
}

/** In-process scheduler liveness — lets the UI flag a dead tick loop. */
export function useSchedulerStatus() {
  return useQuery<SchedulerStatus>({
    queryKey: ['admin-scheduler-status'],
    queryFn: () => apiFetch<SchedulerStatus>('/api/admin/scheduler-status'),
    staleTime: 5 * 1000,
    refetchInterval: 15 * 1000,
  });
}

export interface FireNowResponse {
  status: string;
  schedule_id: number;
  schedule_name: string;
  actions: string[];
}

/** Manually trigger a schedule's actions outside the cron cadence. */
export function useFireScheduleNow() {
  const qc = useQueryClient();
  return useMutation<FireNowResponse, Error, number>({
    mutationFn: (id) =>
      apiFetch<FireNowResponse>(`/api/admin/schedules/${id}/fire-now`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-schedule-runs'] });
      qc.invalidateQueries({ queryKey: ['admin-scheduler-status'] });
    },
  });
}

export interface ScheduleRunsFilters {
  scheduleId?: number;
  /** Reuses the row union rather than restating it — a third copy of the status vocabulary is how
   *  the panel's filter came to omit `skipped` while the badge rendered it. */
  status?: ScheduleRunStatus;
  limit?: number;
}

/**
 * Recent invocations from the in-app scheduler. Refetches every 30s so the
 * panel stays roughly fresh without flooding the server during a long
 * Admin-tab session. Per-tick precision isn't needed — admins typically
 * scan this when triaging, not as a live feed.
 */
export function useScheduleRuns(filters: ScheduleRunsFilters = {}) {
  const params = new URLSearchParams();
  if (filters.scheduleId != null) params.set('schedule_id', String(filters.scheduleId));
  if (filters.status) params.set('status', filters.status);
  if (filters.limit != null) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return useQuery<ScheduleRunsResponse>({
    queryKey: ['admin-schedule-runs', filters],
    queryFn: () =>
      apiFetch<ScheduleRunsResponse>(
        `/api/admin/schedule-runs${qs ? `?${qs}` : ''}`,
      ),
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

/**
 * Audit-log timeline for any audited entity (currently 'suggestion' or
 * 'intel'). Powers the <AuditHistory> component. Admin-only on the
 * server. Refetches on window focus so newly-landed audit rows from
 * other admins appear without manual reload.
 */
export function useAuditLog(entityType: AuditEntityType, entityId: number | null) {
  return useQuery<AuditLogResponse>({
    queryKey: ['audit', entityType, entityId],
    queryFn: () =>
      apiFetch<AuditLogResponse>(`/api/admin/audit/${entityType}s/${entityId}`),
    enabled: entityId != null,
    staleTime: 15 * 1000,
  });
}


// ────────────────────────────────────────────────────────────────────────
// Maintenance actions — one-shot admin endpoints invoked from the
// Admin tab's Maintenance section. Each action is a POST to an
// idempotent backend endpoint that returns a JSON result summary.
// ────────────────────────────────────────────────────────────────────────

// ── Bound-but-new-deal-warranted event backfill ───────────────────────────
//
// Recovers from the legacy uq_deal_event_log_source constraint that
// blocked the carve-out INSERT for signals where the classifier asserted
// `bound_but_new_deal_warranted=True`. Re-runs the carve-out per signal
// using the cached classifier output — no Anthropic spend.

export interface BnbdwBackfillResult {
  candidates: number;
  backfilled: number;
  bound_to_affinity_opp?: number;
  already_present: number;
  no_candidate_matched: number;
  skipped_no_extraction: number;
  errors: { signal_id: number; error: string }[];
}

export function useBackfillBnbdwEvents() {
  return useMutation<BnbdwBackfillResult, Error, { limit?: number }>({
    mutationFn: (body) =>
      apiFetch<BnbdwBackfillResult>(
        `/api/admin/maintenance/backfill-bnbdw-events`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
  });
}


// ── Over-created candidate migration ──────────────────────────────────────
//
// Recovery for the 2026-05-26 over-creation incident: the previous
// bnbdw backfill (PR #188) skipped post-classifier match and created
// candidates that should have bound to existing Affinity opps.

export interface CandidateMigration {
  candidate_uuid: string;
  target_opp_id: number | null;
  events_migrated: number;
  events_no_target: number;
  candidate_dismissed: boolean;
  error: string | null;
}

export interface MigrationResult {
  candidates_scanned: number;
  candidates_dismissed: number;
  events_migrated: number;
  per_candidate: CandidateMigration[];
  errors: { candidate_uuid: string; error: string }[];
}

export function useMigrateOverCreatedCandidates() {
  return useMutation<MigrationResult, Error, { candidate_uuid?: string; limit?: number }>({
    mutationFn: (body) =>
      apiFetch<MigrationResult>(
        `/api/admin/maintenance/migrate-over-created-candidates`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
  });
}


// ── Deal identity repair ───────────────────────────────────────────────────
//
// Moves explicit `Project X` values out of company_name slots and into
// project_codename/codename. Conservative cleanup for the Phase B deal
// identity field fix.

export interface DealIdentityRepairResult {
  audit_id: number | null;
  dry_run: boolean;
  candidates_scanned: number;
  candidates_repaired: number;
  candidates_skipped: number;
  suggestions_scanned: number;
  suggestions_repaired: number;
  candidate_updates: Record<string, unknown>[];
  suggestion_updates: Record<string, unknown>[];
  errors: Record<string, unknown>[];
}

export interface DealIdentityRepairRequest {
  candidate_uuid?: string;
  limit?: number;
  dry_run: boolean;
}

export function useRepairDealIdentity() {
  const qc = useQueryClient();
  return useMutation<DealIdentityRepairResult, Error, DealIdentityRepairRequest>({
    mutationFn: (body) =>
      apiFetch<DealIdentityRepairResult>(
        `/api/admin/maintenance/repair-deal-identity`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: (data) => {
      if (!data.dry_run) {
        qc.invalidateQueries({ queryKey: ['deals'] });
        qc.invalidateQueries({ queryKey: ['maintenance-audit-log'] });
      }
    },
  });
}


// ── Canonical codename backfill ────────────────────────────────────────────
//
// Re-keys open candidates' codename cluster keys to canonical form (strips the
// "Project " prefix) and merges the duplicate pairs that reveals. Auto-merge
// only when every candidate in a collision shares one company name; the rest →
// review. Phase 3 of the deal-identity refactor.

export interface CanonicalCodenameBackfillResult {
  audit_id: number | null;
  dry_run: boolean;
  candidates_scanned: number;
  rekeyed: Record<string, unknown>[];
  merged: Record<string, unknown>[];
  review: Record<string, unknown>[];
  errors: Record<string, unknown>[];
}

export function useBackfillCanonicalCodenameKeys() {
  const qc = useQueryClient();
  return useMutation<CanonicalCodenameBackfillResult, Error, { dry_run: boolean }>({
    mutationFn: (body) =>
      apiFetch<CanonicalCodenameBackfillResult>(
        `/api/admin/maintenance/backfill-canonical-codename-keys`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: (data) => {
      if (!data.dry_run) {
        qc.invalidateQueries({ queryKey: ['deals'] });
        qc.invalidateQueries({ queryKey: ['maintenance-audit-log'] });
      }
    },
  });
}


// ── NDA first-turn corpus backfill (lifecycle PR 4B-2a §4.1) ───────────────
//
// Re-runs the deterministic first-turn calibration capture for every review
// whose turn 1 has a captured peter_sent document. No model calls; idempotent.
// Counts only — never NDA content (viewing stays NDA-privilege-gated).

export interface NdaBackfillCorpusResult {
  dry_run: boolean;
  audit_id?: number | null;
  scanned: number;
  complete: number;
  missing_markup: number;
  missing_peter: number;
  diff_failed: number;
  skipped: number;
}

export function useNdaBackfillCorpus() {
  const qc = useQueryClient();
  return useMutation<NdaBackfillCorpusResult, Error, { dry_run: boolean; review_id?: number }>({
    mutationFn: (body) =>
      apiFetch<NdaBackfillCorpusResult>(
        `/api/admin/maintenance/nda-backfill-corpus`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: (data) => {
      if (!data.dry_run) {
        qc.invalidateQueries({ queryKey: ['maintenance-audit-log'] });
      }
    },
  });
}

// ── NDA CRM-linkage backfill (PR-2) ────────────────────────────────────────
export interface NdaBackfillCrmLinksResult {
  dry_run: boolean;
  audit_id?: number | null;
  scanned: number;
  resolved: number;
}

export function useNdaBackfillCrmLinks() {
  const qc = useQueryClient();
  return useMutation<NdaBackfillCrmLinksResult, Error, { dry_run: boolean; review_id?: number }>({
    mutationFn: (body) =>
      apiFetch<NdaBackfillCrmLinksResult>(
        `/api/admin/maintenance/nda-backfill-crm-links`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: (data) => {
      if (!data.dry_run) {
        qc.invalidateQueries({ queryKey: ['maintenance-audit-log'] });
      }
    },
  });
}


// ── NDA outbound discovery trigger (lifecycle PR 4B-2a) ────────────────────
//
// One-off run of the scan_nda_outbound discovery action. Counts only. No-ops
// (scanned=0) unless nda_sent_mail_discovery_enabled; byte-fetch/similarity
// need nda_blob_fetch_enabled; auto-bind stays gated.

export interface NdaOutboundDiscoveryResult {
  audit_id?: number | null;
  discovery_enabled: boolean;
  blob_fetch_enabled: boolean;
  auto_bind_enabled: boolean;
  scanned: number;
  already_processed: number;
  decisions: Record<string, number>;
}

export function useRunNdaOutboundDiscovery() {
  const qc = useQueryClient();
  return useMutation<NdaOutboundDiscoveryResult, Error, { limit?: number }>({
    mutationFn: (body) =>
      apiFetch<NdaOutboundDiscoveryResult>(
        `/api/admin/maintenance/nda-run-outbound-discovery`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['maintenance-audit-log'] }),
  });
}


// ── NDA obligations sweep (lifecycle PR 4B-2c §4.2) ─────────────────────────
//
// On-demand executed-NDA obligations extraction. No-ops (disabled) unless the
// nda_obligation_extraction_enabled flag is on. SPENDS LLM budget — no dry run.

export interface NdaObligationsSweepResult {
  disabled: boolean;
  audit_id?: number | null;
  extracted?: number;
  skipped?: number;
  failed?: number;
  budget_deferred?: number;
}

export function useNdaRunObligationsSweep() {
  const qc = useQueryClient();
  return useMutation<NdaObligationsSweepResult, Error, { limit?: number }>({
    mutationFn: (body) =>
      apiFetch<NdaObligationsSweepResult>(
        `/api/admin/maintenance/nda-run-obligations-sweep`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: (data) => {
      if (!data.disabled) {
        qc.invalidateQueries({ queryKey: ['maintenance-audit-log'] });
      }
    },
  });
}


// ── NDA bulk-bind pending outbound candidates (lifecycle PR 4B-2a) ──────────
//
// Re-runs the entitled fetch + apply for each pending outbound bind candidate,
// oldest-first per matter (earliest seeds the turn-1 corpus, latest becomes
// canonical). Counts + per-candidate outcomes only. Disabled unless
// nda_blob_fetch_enabled.

export interface NdaBindOutboundOutcome {
  work_item_id: number;
  status: string;
  review_id?: number | null;
  captured_document_id?: number | null;
  detail?: string;
}

export interface NdaBindOutboundResult {
  disabled?: boolean;
  audit_id?: number | null;
  scanned?: number;
  captured?: number;
  ambiguous?: number;
  no_document?: number;
  no_turn?: number;
  already_bound?: number;
  skipped_no_review?: number;
  error?: number;
  outcomes?: NdaBindOutboundOutcome[];
}

export function useBindOutboundCandidates() {
  const qc = useQueryClient();
  return useMutation<NdaBindOutboundResult, Error, { review_id?: number }>({
    mutationFn: (body) =>
      apiFetch<NdaBindOutboundResult>(
        `/api/admin/maintenance/nda-bind-outbound-candidates`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: (data) => {
      if (!data.disabled) {
        qc.invalidateQueries({ queryKey: ['maintenance-audit-log'] });
      }
    },
  });
}


// ── Phase B nuclear reset ──────────────────────────────────────────────────

export interface NuclearResetRequest {
  scope: 'phase_b_only' | 'phase_a_b';
  confirm_phrase: string;
  reset_graph_sync: boolean;
  audit_note?: string | null;
}

export interface NuclearResetResult {
  status: string;
  audit_id: number;
  scope: 'phase_b_only' | 'phase_a_b';
  actor_email: string;
  rows_truncated: Record<string, number>;
  anthropic_usage_log_fk_nulled: number;
  graph_sync_state_truncated: number;
  started_at: string;
  finished_at: string | null;
}

export interface MaintenanceAuditEntry {
  id: number;
  action: string;
  actor_email: string;
  scope: string | null;
  audit_note: string | null;
  rows_truncated_json: string | null;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'ok' | 'failed';
  error: string | null;
}

/**
 * Destructive: truncates Phase A/B derived tables. Admin-only; requires
 * the literal confirm_phrase in the body. The endpoint refuses with 409
 * when there are active aggregator runs.
 */
export function useNuclearResetPhaseB() {
  const qc = useQueryClient();
  return useMutation<NuclearResetResult, Error, NuclearResetRequest>({
    mutationFn: (body) =>
      apiFetch<NuclearResetResult>(
        '/api/admin/maintenance/phase-b/nuclear-reset',
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      // Everything Phase-A/B-derived just went away — invalidate the
      // dashboards that read from those tables so the FE re-fetches
      // and shows clean state.
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['signals-triage'] });
        qc.invalidateQueries({ queryKey: ['maintenance-audit-log'] });
    },
  });
}

export function useMaintenanceAuditLog(limit = 20) {
  return useQuery<{ entries: MaintenanceAuditEntry[] }>({
    queryKey: ['maintenance-audit-log', limit],
    queryFn: () =>
      apiFetch<{ entries: MaintenanceAuditEntry[] }>(
        `/api/admin/maintenance/audit-log?limit=${limit}`,
      ),
    staleTime: 30_000,
  });
}


// ── Learning Portal (B-3b) ─────────────────────────────────────────────────

import type {
  LearningDismissalsResponse,
  LearningEditsResponse,
  LearningOverviewResponse,
} from '../types';

export function useLearningOverview(sinceDays: number) {
  return useQuery<LearningOverviewResponse>({
    queryKey: ['learning-portal', 'overview', sinceDays],
    queryFn: () =>
      apiFetch<LearningOverviewResponse>(
        `/api/admin/learning-portal/overview?since_days=${sinceDays}`,
      ),
    staleTime: 60_000,
  });
}

interface DismissalsFilters {
  sinceDays?: number;
  granularity?: 'deal' | 'field';
  reason?: string;
  limit?: number;
  offset?: number;
}

export function useLearningDismissals(filters: DismissalsFilters) {
  const params = new URLSearchParams();
  if (filters.sinceDays != null) params.set('since_days', String(filters.sinceDays));
  if (filters.granularity) params.set('granularity', filters.granularity);
  if (filters.reason) params.set('reason', filters.reason);
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return useQuery<LearningDismissalsResponse>({
    queryKey: ['learning-portal', 'dismissals', filters],
    queryFn: () =>
      apiFetch<LearningDismissalsResponse>(
        `/api/admin/learning-portal/dismissals${qs ? `?${qs}` : ''}`,
      ),
    staleTime: 30_000,
  });
}

interface EditsFilters {
  sinceDays?: number;
  fieldName?: string;
  limit?: number;
  offset?: number;
}

export function useLearningEdits(filters: EditsFilters) {
  const params = new URLSearchParams();
  if (filters.sinceDays != null) params.set('since_days', String(filters.sinceDays));
  if (filters.fieldName) params.set('field_name', filters.fieldName);
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return useQuery<LearningEditsResponse>({
    queryKey: ['learning-portal', 'edits', filters],
    queryFn: () =>
      apiFetch<LearningEditsResponse>(
        `/api/admin/learning-portal/edits${qs ? `?${qs}` : ''}`,
      ),
    staleTime: 30_000,
  });
}


// ── Maintenance: rescan recent emails ──────────────────────────────────────

export interface RescanPerUser {
  user_email: string;
  source: string;
  scanned: number;
  kept: number;
  discarded: number;
  deal_resolve_attempted: number;
  deal_resolve_bound: number;
  cost_usd: number;
  error: string | null;
}

export interface RescanResult {
  dry_run: boolean;
  days: number;
  source: string;
  force_reclassify: boolean;
  users: string[];
  per_user: RescanPerUser[];
  total_scanned: number;
  total_kept: number;
  total_cost_usd: number;
  errors: Record<string, string>;
}

export interface RescanRequest {
  users?: string[];
  all_enabled?: boolean;
  days: number;
  source: 'both' | 'inbox' | 'sent';
  force_reclassify: boolean;
  dry_run: boolean;
}

// Queue-only Phase B (owner ruling 2026-08-20) — a `dry_run: true` call still
// answers inline with the plan (it reaches no scan); a real rescan is now
// queued through the same `rescan_recent_emails` job type the dedicated
// enqueue endpoint below uses, and returns the queue receipt instead of the
// scan summary.
export interface RescanQueuedResponse {
  job_id: number;
  status: string;
  queued: true;
  note: string;
}

export function useRescanRecentEmails() {
  return useMutation<RescanResult | RescanQueuedResponse, Error, RescanRequest>({
    mutationFn: (body) =>
      apiFetch<RescanResult | RescanQueuedResponse>(`/api/admin/rescan-recent-emails`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}


// ── Maintenance background jobs ─────────────────────────────────────────────
//
// Persistent queue for long-running ops (rescan, future replay-harness
// captures). Enqueue returns a job id; the UI polls /maintenance/jobs/{id}
// until status is terminal (ok / failed / killed_by_restart).

export type MaintenanceJobStatus =
  | 'queued'
  | 'running'
  | 'ok'
  | 'failed'
  | 'killed_by_restart';

export interface MaintenanceJobProgress {
  phase?: string;
  total_units?: number;
  completed_units?: number;
  users_total?: number;
  users_done?: number;
  current_user?: string | null;
}

export interface MaintenanceJob {
  id: number;
  job_type: string;
  args: Record<string, unknown> | null;
  status: MaintenanceJobStatus;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  output: unknown;
  progress: MaintenanceJobProgress | null;
  /**
   * INTRA-DEAL live progress, present only while a deal of THIS job is in flight. `progress` above is
   * the durable per-deal checkpoint and cannot advance mid-deal, which is why a slow deal and a wedged
   * worker used to look identical from the panel — the Phase B replay was restarted by hand three
   * times on that ambiguity. Read from the worker's process memory, so it disappears the moment the
   * deal ends and is never present for a finished job.
   */
  live: MaintenanceJobLive | null;
  error: string | null;
  created_by: string;
}

export interface MaintenanceJobLive {
  job_id: number;
  deal_ref: string;
  deal_index: number;
  cohort_total: number;
  stage: string;
  elapsed_s: number;
  /** The per-deal wall-clock budget; 0 means the watchdog is disabled for this run. */
  budget_s: number;
  messages_done: number;
  messages_total: number;
  llm_calls: number;
}

export interface MaintenanceJobsResponse {
  jobs: MaintenanceJob[];
}

export interface EnqueueRescanResponse {
  job_id: number;
  status: 'queued';
}

export function useEnqueueRescanJob() {
  const qc = useQueryClient();
  return useMutation<EnqueueRescanResponse, Error, RescanRequest>({
    mutationFn: (body) =>
      apiFetch<EnqueueRescanResponse>(
        `/api/admin/maintenance/jobs/rescan-recent-emails`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'maintenance-jobs'] });
    },
  });
}

/**
 * Poll a single maintenance job until it reaches a terminal status.
 * `refetchInterval` returns false once the job is terminal, which stops
 * react-query from making further requests.
 */
export function useMaintenanceJob(jobId: number | null) {
  return useQuery<MaintenanceJob, Error>({
    queryKey: ['admin', 'maintenance-job', jobId],
    queryFn: () =>
      apiFetch<MaintenanceJob>(`/api/admin/maintenance/jobs/${jobId}`),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      // A job that cannot be READ is not a job that is still running: without this, a panel whose
      // GET keeps failing (deleted row, 404, a permission change mid-run) polls every 2s forever
      // behind its error message.
      if (query.state.error) return false;
      const data = query.state.data;
      if (!data) return 2000;
      if (data.status === 'queued' || data.status === 'running') return 2000;
      return false;
    },
    refetchIntervalInBackground: false,
  });
}

export function useMaintenanceJobs(limit: number = 20, jobType?: string) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (jobType) params.set('job_type', jobType);
  return useQuery<MaintenanceJobsResponse, Error>({
    queryKey: ['admin', 'maintenance-jobs', limit, jobType ?? 'all'],
    queryFn: () =>
      apiFetch<MaintenanceJobsResponse>(
        `/api/admin/maintenance/jobs?${params.toString()}`,
      ),
    refetchInterval: 5000,
  });
}


// ── Admin → Maintenance → "Run POST tool" (registry-driven runner) ─────────
//
// The whole point of these two hooks is that there will never be a third.
// The backend publishes a hardcoded allowlist of maintenance actions as DATA
// (`app/services/maintenance/tool_registry.py`); the modal renders whatever
// comes back and posts the id the operator picked. Exposing the next endpoint
// is a registry entry in a backend PR — no hook, no import, no component here.

/** One typed parameter, as DECLARED by the backend registry. The modal builds
 *  its inputs from this and nothing else — there is no per-tool frontend code,
 *  so the next parameterised tool costs zero change in this file. */
export interface MaintenanceToolParameter {
  name: string;
  type: 'bool' | 'int' | 'string' | 'enum' | 'string_list';
  label: string;
  help: string;
  default: unknown;
  required: boolean;
  nullable: boolean;
  /** Closed value set — always present for `enum`, optional for `string_list`. */
  choices: string[];
  minimum: number | null;
  maximum: number | null;
  /** Character ceiling for a `string` (or each member of a `string_list`); null when unbounded.
   *  The BACKEND is the gate — it rejects an over-long value before the run is even audited, which
   *  is why the bound lives on the registry declaration and not on the shipped route. This is here
   *  so the input can say so up front rather than letting an operator type 600 characters and lose
   *  them to a 422. */
  max_length: number | null;
}

/** Which single parameter turns this form from a preview into a write.
 *  PRESENTATION ONLY: the backend route owns the gate that actually decides
 *  whether anything is written. This is what the modal says out loud, and what
 *  makes the acknowledgement tick appear. */
export interface MaintenanceToolWriteArm {
  parameter: string;
  armed_when: 'true' | 'set';
  armed_note: string;
  disarmed_note: string;
}

export interface MaintenanceTool {
  id: string;
  label: string;
  description: string;
  group: string;
  supports_dry_run: boolean;
  parameters: MaintenanceToolParameter[];
  write_arm?: MaintenanceToolWriteArm | null;
}

export interface MaintenanceToolsResponse {
  tools: MaintenanceTool[];
}

export interface MaintenanceToolRunResult {
  tool_id: string;
  label: string;
  dry_run: boolean;
  audit_id: number;
  /** The parameters the server actually validated and ran with — echoed back so
   *  the operator sees what happened, not what they think they typed. */
  params?: Record<string, unknown>;
  write_armed?: boolean;
  /** Whatever the tool returned. Deliberately unknown — a registry-driven
   *  runner cannot know the shape, so the modal renders it as JSON. */
  result: unknown;
}

export function useMaintenanceTools(enabled: boolean) {
  return useQuery<MaintenanceToolsResponse, Error>({
    queryKey: ['admin', 'maintenance-tools'],
    queryFn: () => apiFetch<MaintenanceToolsResponse>('/api/admin/maintenance/tools'),
    enabled,
    staleTime: 60_000,
  });
}

export function useRunMaintenanceTool() {
  const qc = useQueryClient();
  return useMutation<
    MaintenanceToolRunResult,
    Error,
    { toolId: string; dryRun: boolean; params?: Record<string, unknown> }
  >({
    mutationFn: ({ toolId, dryRun, params }) =>
      apiFetch<MaintenanceToolRunResult>(
        `/api/admin/maintenance/tools/${encodeURIComponent(toolId)}/run`,
        {
          method: 'POST',
          body: JSON.stringify({ dry_run: dryRun, params: params ?? {} }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['maintenance-audit-log'] });
    },
  });
}


// ── Admin → Reports → Fact proposal review ────────────────────────────────

export interface FactProposalCurrentFact {
  id: number;
  relationship_type: string;
  qualifiers: Record<string, string>;
  decision_state: string;
  decided_via: string | null;
  producer_kind: string;
  confidence: string | null;
  observed_at: string | null;
  human: boolean;
}

export interface FactProposalSlot {
  key: string;
  value: string | null;
  confidence: string | null;
  verdict: string;
}

export interface FactProposalEvidenceRef {
  kind: string;
  ref: number;
}

export interface FactProposalReviewRow {
  review_id: number;
  created_at: string;
  entry_id: number;
  list_id: number | null;
  deal_name: string | null;
  party_kind: 'person' | 'organization';
  party_id: number | null;
  party_name: string | null;
  current_facts: FactProposalCurrentFact[];
  proposed_slots: FactProposalSlot[];
  evidence_refs: FactProposalEvidenceRef[];
  flags: string[];
  action: string | null;
  outcome: string | null;
  binding_state: string | null;
  vocabulary_snapshot_id: number | null;
}

export interface FactProposalReviewResponse {
  read_only: true;
  window_days: number;
  generated_at: string;
  fixture: { verified: boolean; reason: string };
  summary: {
    stored_proposals: number;
    current_party_rows: number;
    flagged_rows: number;
    flag_counts: Record<string, number>;
    attach: Record<string, number>;
  };
  rows: FactProposalReviewRow[];
  truncated: boolean;
}

export function useFactProposalReviewReport(days: number, enabled = true) {
  return useQuery<FactProposalReviewResponse, Error>({
    queryKey: ['admin', 'reports', 'fact-proposals', days],
    queryFn: () => apiFetch<FactProposalReviewResponse>(
      `/api/admin/reports/fact-proposals?days=${days}&limit=500`,
    ),
    enabled,
    staleTime: 60_000,
  });
}
